import React, { useContext, useRef } from 'react';

import ChevronLeft from '@atlaskit/icon/glyph/chevron-left';
import ChevronRight from '@atlaskit/icon/glyph/chevron-right';
import { Box, xcss } from '@atlaskit/primitives';

import FileTree from 'src/components/diff-set/diff-file-tree';
import { ExtendedMouseEvent } from 'src/components/sidebar';
import { useIntl } from 'src/hooks/intl';

import { KeyboardShortcutTooltip } from '../shortcuts/keyboard-shortcut-tooltip';
import { KeyboardShortcutsContext } from '../shortcuts/pull-request-keyboard-shortcuts-context';
import { keyboardShortcutMessages } from '../shortcuts/pull-request-keyboard-shortcuts.i18n';

import { messages } from './diff-tab.i18n';
import {
  CollapseButton,
  Sidebar,
  SidebarContainer,
  SidebarContent,
  SidebarControls,
  shadowStyles,
} from './diff-tab.style';
import { FileTreeContext } from './file-tree-context';
import { saveDiffSidebarPrefs } from './utils';

export const fileTreeStyles = xcss({
  borderColor: 'color.border',
  borderRadius: 'border.radius.100',
  borderStyle: 'solid',
  borderWidth: 'border.width',
});

export const FileTreeSidebar = (): JSX.Element => {
  const intl = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  const {
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    sidebarWidth,
    setSidebarWidth,
  } = useContext(FileTreeContext);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
    saveDiffSidebarPrefs(sidebarWidth, !isSidebarCollapsed);
  };

  const { toggleFileTreeRef } = useContext(KeyboardShortcutsContext);
  if (toggleFileTreeRef) {
    toggleFileTreeRef.current = () => {
      setIsSidebarCollapsed(!isSidebarCollapsed);
    };
  }

  const onMouseMove = (e: ExtendedMouseEvent): void => {
    const isLeftButton = (e.buttons || e.which) === 1;
    const { pageX } = e;
    // Calculate the offset of the sidebar from the left of the page and the padding of the controls
    const offset = (ref.current?.getBoundingClientRect().left || 0) + 12;
    if (isLeftButton) {
      const width = pageX - offset;
      setSidebarWidth(width);
      saveDiffSidebarPrefs(width, isSidebarCollapsed);
    }
  };

  const onMouseUp = (): void => {
    document.removeEventListener('mouseup', onMouseUp);
    document.removeEventListener('mousemove', onMouseMove);
  };

  const onMouseDown = (): void => {
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mousemove', onMouseMove);
  };

  const SidebarToggleButton = (
    <CollapseButton
      onClick={toggleSidebar}
      aria-label={intl.formatMessage(messages.toggleFileView)}
      isSidebarCollapsed={isSidebarCollapsed}
      // Allows the button to be tab focused when the sidebar is collapsed
      tabIndex={isSidebarCollapsed ? 0 : -1}
      // Prevents focus staying attached to the button when pressed
      onMouseDown={e => e.preventDefault()}
    >
      {isSidebarCollapsed ? (
        <ChevronRight label="" />
      ) : (
        <ChevronLeft label="" />
      )}
    </CollapseButton>
  );

  return (
    <SidebarContainer aria-expanded={!isSidebarCollapsed}>
      <SidebarControls
        onMouseDown={onMouseDown}
        isSidebarCollapsed={isSidebarCollapsed}
        role="slider"
        aria-orientation="vertical"
        aria-controls="file-tree"
      >
        <KeyboardShortcutTooltip
          text={
            isSidebarCollapsed
              ? keyboardShortcutMessages.fileTreeToggleExpandShortcut
              : keyboardShortcutMessages.fileTreeToggleCollapseShortcut
          }
          shortcut="f"
          position="right"
          hideTooltipOnClick
        >
          {SidebarToggleButton}
        </KeyboardShortcutTooltip>
        <Box xcss={shadowStyles} />
      </SidebarControls>
      {!isSidebarCollapsed && (
        <Box
          role="region"
          aria-label={intl.formatMessage(messages.fileViewLabel)}
          xcss={fileTreeStyles}
        >
          <Sidebar width={sidebarWidth} id="file-tree" ref={ref}>
            <SidebarContent>
              <FileTree />
            </SidebarContent>
          </Sidebar>
        </Box>
      )}
    </SidebarContainer>
  );
};
